import React, { useEffect, useState } from 'react'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CircularProgress,
  Grid,
  Typography
} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AlertDialog, EnhancedAccordion, IconButtonWithTooltip, LastUpdatedComponent } from 'components'
import { type BaseBackgroundInfo } from 'services'
import { type HttpResponse } from 'interfaces'

interface BiographyAccordionProps <T extends BaseBackgroundInfo> {
  index: number
  originalData: T
  createCard: boolean
  modelName: string
  onExpandedChange: (isExpanded: boolean) => void
  getCondensedInfo: (data: T) => React.ReactNode
  getExpandedInfo: (data: T, disabled: boolean, onDataChange: (newData: T) => void) => React.ReactNode
  onSave: (data: T) => Promise<HttpResponse<T>>
  onRemove?: (data: T) => void
  onDelete: (data: T) => Promise<void>
}

export const BackgroundInfoCard: React.FC<BiographyAccordionProps<BaseBackgroundInfo>> = <T extends BaseBackgroundInfo>({
  index,
  originalData,
  createCard,
  onExpandedChange,
  modelName,
  getCondensedInfo,
  getExpandedInfo,
  onSave,
  onRemove,
  onDelete
}: BiographyAccordionProps<T>) => {
  const [expanded, setExpanded] = useState(createCard)
  const [disabled, setDisabled] = useState<boolean>(!createCard)
  const [currentData, setCurrentData] = useState<T>(originalData)
  const [saving, setSaving] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  useEffect(() => {
    setCurrentData(originalData)
    if (createCard) {
      setExpanded(true)
      setDisabled(false)
      onExpandedChange(true)
    }
  }, [originalData, createCard])

  const handleEdit = (): void => {
    setDisabled(false)
    setExpanded(true)
    onExpandedChange(true)
  }

  const handleCancel = (): void => {
    if (currentData.id === 0 && onRemove != null) {
      onRemove(currentData)
    } else {
      setCurrentData(originalData)
      setDisabled(true)
      setExpanded(false)
      onExpandedChange(false)
    }
  }

  const handleSave = async (): Promise<void> => {
    setSaving(true)
    const response = await onSave(currentData)
    setSaving(false)
    if (response.succeeded) {
      setDisabled(true)
      setExpanded(false)
      onExpandedChange(false)
    }
  }

  const handleDelete = async (): Promise<void> => {
    setSaving(true)
    await onDelete(currentData)
    setSaving(false)
  }

  const handleDataChange = (newData: T): void => {
    setCurrentData(newData)
  }

  const renderButtons = (): React.ReactNode => {
    return (

      saving
        ? <CircularProgress/>
        : <>
        {disabled && (
          <IconButtonWithTooltip
            label={`Edit ${modelName}`}
            color="primary"
            onClick={handleEdit}
            icon={<EditIcon/>}
          />
        )}
        {!disabled && (
          <IconButtonWithTooltip
            label="Cancel Editing"
            color="secondary"
            onClick={handleCancel}
            icon={<CancelIcon/>}
          />
        )}
        {!disabled && (
          <IconButtonWithTooltip
            label={`Save ${modelName}`}
            color="primary"
            onClick={ () => { void handleSave() }}
            disabled={saving}
            icon={<SaveIcon/>}
          />
        )}
        <IconButtonWithTooltip
          label={`Delete ${modelName}`}
          color="secondary"
          disabled={saving || currentData.id === 0}
          icon={<DeleteIcon/>}
          onClick={ () => { setShowDeleteConfirmation(true) }}
        />
      </>
    )
  }

  return (
    <Card key={`kw-${modelName}-${index}`}>
      <EnhancedAccordion
          expanded={expanded}
          onExpandedChange={(isExpanded) => {
            setExpanded(isExpanded)
            onExpandedChange(isExpanded)
          }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
        <Grid container alignItems="center" spacing={1}>
            <Grid item xs= {8}>
              <Typography
                variant="body1"
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: 2
                }}
              >
                <Typography variant="body1" component="span" fontWeight="bold">
                  {`${modelName} ${currentData.id === 0 ? '(New)' : `#${currentData.displayOrder}`} - `}
                </Typography>
                {!expanded && getCondensedInfo(currentData)}
              </Typography>
            </Grid>
          <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LastUpdatedComponent value={currentData.lastUpdated}/>
          </Grid>
          {!expanded && (<Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {renderButtons()}
          </Grid>)}
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        {expanded && (
          <>
          {getExpandedInfo(currentData, disabled, handleDataChange)}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {renderButtons()}
          </Box>
          </>
        )}
      </AccordionDetails>
    </EnhancedAccordion>
      <AlertDialog
          onClose={() => { setShowDeleteConfirmation(false) }}
          open={showDeleteConfirmation}
          onButtonClick={ () => { void handleDelete() }}
          title={`${modelName} Deletion`}
          contentText={`Are you sure you want to delete this ${modelName}? This action cannot be undone.`}
          nameButtonCancel='Close'
          nameButtonConfirm='Delete'
      />
    </Card>)
}
